import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { projectsAPI, mbaAPI, getImageUrl } from "../utils/api";
import "../styles/ProjectDetailPremium.css";
import { API_BASE_URL as CONFIG_API_URL } from '../utils/apiConfig';
const API_BASE_URL = CONFIG_API_URL.replace(/\/api\/?$/, '');



// Full HTML capture (used to open a standalone detailed page in a new tab)
export const PREMIUM_HTML_TEMPLATE = ({ title, specialization, project_type, original_price = '₹4999', discount_price = '₹2999', author = '', project = {} }) => {
  const docFileName = project.doc_file_name || (project.doc_file ? project.doc_file.split('/').pop() : 'Project_Report.docx');
  const pdfFileName = project.pdf_file_name || project.report_file_name || (project.pdf_file ? project.pdf_file.split('/').pop() : (project.report_file ? project.report_file.split('/').pop() : 'Project_Report.pdf'));

  const abstractText = project.abstract || project.description || 'No description available.';

  const originalPriceVal = Number(project.originalPrice || 0);
  const discountedPriceVal = Number(project.discountedPrice || 0);
  const isDiscountActiveVal = Boolean(project.isDiscountActive);

  const showDiscount = isDiscountActiveVal && originalPriceVal > 0 && discountedPriceVal < originalPriceVal;

  let pricingCardHtml = '';
  if (showDiscount) {
    const discountPercentage = Math.round(((originalPriceVal - discountedPriceVal) / originalPriceVal) * 100);
    pricingCardHtml = `
      <div class="w-full h-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-2 flex flex-col justify-center items-center text-center shadow-sm min-h-[56px] min-w-[160px]">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Price</span>
        <div class="flex items-baseline justify-center gap-2">
          <span class="text-xl font-extrabold text-emerald-600">₹${discountedPriceVal.toLocaleString()}</span>
          <span class="text-xs font-semibold text-slate-400 line-through">₹${originalPriceVal.toLocaleString()}</span>
        </div>
        <div class="mt-0.5">
          <span class="inline-block px-2 py-0.5 text-[9px] font-extrabold text-emerald-700 bg-emerald-50 rounded-full border border-emerald-100">${discountPercentage}% OFF</span>
        </div>
      </div>
    `;
  } else {
    pricingCardHtml = `
      <div class="w-full h-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-2 flex flex-col justify-center items-center text-center shadow-sm min-h-[56px] min-w-[160px]">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Price</span>
        <span class="text-xl font-extrabold text-slate-800">₹${originalPriceVal.toLocaleString()}</span>
      </div>
    `;
  }

  return `<!doctype html>
<html lang="en" class="h-full">
 <head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title || 'MBA Project Details'}</title>
  <script src="https://cdn.tailwindcss.com/3.4.17"></script>
  <script src="https://cdn.jsdelivr.net/npm/lucide@0.263.0/dist/umd/lucide.min.js"></script>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&amp;display=swap" rel="stylesheet">
  <style>
    * { font-family: 'Inter', sans-serif; }
    .fade-in { animation: fadeIn 0.4s ease; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    body { box-sizing: border-box; }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

    /* List and Formatting styles */
    .project-description-content strong, .project-description-content b {
      font-weight: 800 !important;
      color: #0f172a !important;
    }
    .project-description-content ul {
      list-style-type: disc !important;
      margin: 12px 0 16px !important;
      padding-left: 24px !important;
    }
    .project-description-content ul li {
      list-style-type: disc !important;
      margin-bottom: 6px !important;
    }
    .project-description-content ol {
      list-style-type: decimal !important;
      margin: 12px 0 16px !important;
      padding-left: 24px !important;
    }
    .project-description-content ol li {
      list-style-type: decimal !important;
      margin-bottom: 6px !important;
    }
    .project-description-content h1,
    .project-description-content h2,
    .project-description-content h3,
    .project-description-content h4,
    .project-description-content h5,
    .project-description-content h6 {
      font-size: 1.125rem !important;
      font-weight: 800 !important;
      margin: 16px 0 8px !important;
      color: #0f172a !important;
      display: block !important;
    }
    .project-description-content p {
      margin-bottom: 12px !important;
    }
  </style>
 </head>
 <body class="h-full bg-[#f5f7fa] text-[#0f172a] overflow-auto">
  <div class="w-full min-h-full p-4 md:p-8 lg:p-12">
   <div class="max-w-7xl mx-auto">
    <!-- TITLE (Centered) -->
    <div class="text-center mb-8">
     <h1 class="text-3xl md:text-4xl font-extrabold text-[#0f172a] leading-tight">${title || project.title || 'MBA Project'}</h1>
    </div>

    <!-- TWO-COLUMN ROW: Abstract + Info side by side -->
    <div class="flex flex-col md:flex-row gap-8 items-stretch mb-8">

     <!-- LEFT: PROJECT ABSTRACT -->
     <div class="w-full md:w-[60%] flex flex-col">
      <div class="bg-white rounded-[24px] border border-[#dbe7ee] shadow-sm p-6 md:p-8 transition-all duration-300 hover:shadow-md flex-1">
       <h2 class="text-xl font-bold text-[#0f172a] mb-6 flex items-center gap-2">📄 Description</h2>
        <div class="project-description-content text-sm md:text-base text-slate-800 leading-relaxed font-normal">
          ${abstractText}
        </div>
      </div>
     </div>

     <!-- RIGHT: PROJECT INFORMATION -->
     <div class="w-full md:w-[40%] flex flex-col">
      <div class="bg-white rounded-[24px] border border-[#dbe7ee] shadow-sm p-6 hover:shadow-md transition-all duration-300 flex-1">
       <h2 class="text-lg font-bold text-[#0f172a] mb-5 flex items-center gap-2">📋 Project Information</h2>
       <div class="flex flex-col gap-4">
        <!-- University Level -->
        <div class="flex items-center justify-between border-b border-slate-100 pb-3">
         <div class="flex items-center gap-2 text-slate-500 text-xs"><span class="text-sm">🎓</span> University Level</div>
         <div class="text-xs font-bold text-[#0f172a]">MBA</div>
        </div>
        <!-- Specialization -->
        <div class="flex items-center justify-between border-b border-slate-100 pb-3">
         <div class="flex items-center gap-2 text-slate-500 text-xs"><span class="text-sm">📘</span> Specialization</div>
         <div class="text-xs font-bold text-[#0f172a]">${project.specialization || specialization || 'Finance'}</div>
        </div>
        <!-- Project Type -->
        <div class="flex items-center justify-between border-b border-slate-100 pb-3">
         <div class="flex items-center gap-2 text-slate-500 text-xs"><span class="text-sm">🏷️</span> Project Type</div>
         <div class="text-xs font-bold text-[#0f172a]">${project.type || project_type || 'OJT Project'}</div>
        </div>
        <!-- University -->
        ${project.university ? `
        <div class="flex items-center justify-between border-b border-slate-100 pb-3">
         <div class="flex items-center gap-2 text-slate-500 text-xs"><span class="text-sm">🏛️</span> University</div>
         <div class="text-xs font-bold text-[#0f172a]">${project.university}</div>
        </div>
        ` : ''}
        <!-- Language -->
        <div class="flex items-center justify-between">
         <div class="flex items-center gap-2 text-slate-500 text-xs"><span class="text-sm">🌐</span> Language</div>
         <div class="text-xs font-bold text-[#0f172a]">English</div>
        </div>
       </div>
      </div>
     </div>

    </div>

    <!--
    [PROJECT FILES SECTION]
    <div class="bg-white rounded-[24px] border border-[#dbe7ee] shadow-sm p-6 md:p-8 transition-all duration-300 hover:shadow-md">
     <h2 class="text-xl font-bold text-[#0f172a] mb-6 flex items-center gap-2">📂 Project Files</h2>
     <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      [DOC FILE CARD]
      <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 hover:shadow-md transition-all duration-300 flex flex-col justify-between min-h-[160px] relative hover:-translate-y-1">
       <div class="flex gap-4 items-start">
        <div class="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0"><i data-lucide="file-text" class="w-6 h-6 text-blue-600"></i></div>
        <div class="min-w-0">
         <h3 class="font-bold text-slate-900 text-sm whitespace-nowrap overflow-hidden text-ellipsis mb-1">${docFileName}</h3>
         <span class="text-xs px-2.5 py-1 rounded bg-blue-50 text-blue-700 font-semibold border border-blue-100 uppercase tracking-wider">DOC File • 2.4 MB</span>
        </div>
       </div>
       <div class="flex gap-2 mt-4">
        <button class="download-action-btn flex-1 py-2 rounded-full text-xs font-bold bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 transition-all duration-300" onclick="return window.__mbaDownloadGuard ? window.__mbaDownloadGuard('doc', '${(project.doc_file || '').replace(/'/g, "\\\\'")}') : false">👁 Preview</button>
        <button class="download-action-btn flex-1 py-2 rounded-full text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-blue-200 transition-all duration-300" onclick="return window.__mbaDownloadGuard ? window.__mbaDownloadGuard('doc', '${(project.doc_file || '').replace(/'/g, "\\\\'")}') : false">⬇ Download DOC</button>
       </div>
      </div>

      [PDF FILE CARD]
      <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 hover:shadow-md transition-all duration-300 flex flex-col justify-between min-h-[160px] relative hover:-translate-y-1">
       <div class="flex gap-4 items-start">
        <div class="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0"><i data-lucide="file" class="w-6 h-6 text-red-500"></i></div>
        <div class="min-w-0">
         <h3 class="font-bold text-slate-900 text-sm whitespace-nowrap overflow-hidden text-ellipsis mb-1">${pdfFileName}</h3>
         <span class="text-xs px-2.5 py-1 rounded bg-red-50 text-red-700 font-semibold border border-red-100 uppercase tracking-wider">PDF File • 3.1 MB</span>
        </div>
       </div>
       <div class="flex gap-2 mt-4">
        <button class="download-action-btn flex-1 py-2 rounded-full text-xs font-bold bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 transition-all duration-300" onclick="return window.__mbaDownloadGuard ? window.__mbaDownloadGuard('pdf', '${(project.pdf_file || project.report_file || '').replace(/'/g, "\\\\'")}') : false">👁 Preview</button>
        <button class="download-action-btn flex-1 py-2 rounded-full text-xs font-bold bg-red-600 hover:bg-red-700 text-white shadow-md hover:shadow-red-200 transition-all duration-300" onclick="return window.__mbaDownloadGuard ? window.__mbaDownloadGuard('pdf', '${(project.pdf_file || project.report_file || '').replace(/'/g, "\\\\'")}') : false">⬇ Download PDF</button>
       </div>
      </div>
     </div>
    </div>
    -->

    <!-- ENQUIRY CARD (Full width matching above boxes) -->
    <div class="w-full bg-white rounded-[28px] border border-[#dbe7ee] shadow-md p-8 hover:shadow-lg transition-all duration-300 text-center">
     <h3 class="text-xl font-bold text-slate-800 mb-6">Need This MBA Project?</h3>
     <ul class="text-sm text-slate-600 space-y-2 mb-6 font-medium inline-block text-left">
      <li class="flex items-center gap-2"><span class="text-teal-600 font-bold">✓</span> Get Complete Project Report</li>
      <li class="flex items-center gap-2"><span class="text-teal-600 font-bold">✓</span> Project Documentation</li>
      <li class="flex items-center gap-2"><span class="text-teal-600 font-bold">✓</span> DOC File</li>
      <li class="flex items-center gap-2"><span class="text-teal-600 font-bold">✓</span> PDF File</li>
      <li class="flex items-center gap-2"><span class="text-teal-600 font-bold">✓</span> Project Guidance</li>
     </ul>
     <p class="text-xs text-slate-500 leading-relaxed mb-6">
      Submit an enquiry and our team will contact you with complete details.
     </p>
     <div class="flex flex-col sm:flex-row items-stretch justify-center gap-4 max-w-lg mx-auto">
      <div class="flex-1">
       <button id="enquiryButton" class="w-full h-full py-4 rounded-xl bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 text-white font-bold text-base transition-all duration-300 shadow-md hover:shadow-teal-100 transform hover:-translate-y-0.5" onclick="openEnquiryModal()">Request Project</button>
      </div>
      <div class="flex-1">
       ${pricingCardHtml}
      </div>
     </div>
    </div>

   </div>
   </div>

   <script>
     // Payment Gateway Temporarily Disabled
     // Future Enable Support Retained
     /*
     // Purchase check / redirect
     function proceedToPurchaseFlow() {
       try {
         window.parent.postMessage({ type: 'BUY_NOW' }, '*');
       } catch (msgErr) {}
     }


     // MBA download guard
     window.__mbaDownloadGuard = function(fileType, fileUrl) {
       try {
         var projectId = '${project.id || ''}';
         var orderId = localStorage.getItem('purchase_order_' + projectId);
         var token = orderId ? localStorage.getItem('purchase_token_' + orderId) : null;
         if (!orderId || !token) {
           try {
             window.parent.postMessage({ type: 'BUY_NOW' }, '*');
           } catch (msgErr) {}
           return false;
         }
         window.open(fileUrl, '_blank');
         return false;
       } catch (e) {
         try {
           window.parent.postMessage({ type: 'BUY_NOW' }, '*');
         } catch (msgErr) {}
         return false;
       }
     };

     // Purchase check to toggle active button inner HTML
     const projectId = '${project.id || ''}';
     const price = Number('${project.price || 0}');
     const isFree = !price || price === 0;
     const orderId = localStorage.getItem('purchase_order_' + projectId);
     const token = orderId ? localStorage.getItem('purchase_token_' + orderId) : null;
     const isPurchased = isFree || (orderId && token);

     const buyBtn = document.getElementById('buyButton');
     if (buyBtn) {
       if (isPurchased) {
         buyBtn.innerHTML = '🔓 Project Unlocked';
         buyBtn.disabled = true;
         buyBtn.className = 'w-full py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-bold text-base cursor-not-allowed opacity-80 mb-4';
       }
     }

     if (!isPurchased) {
       document.querySelectorAll('.download-action-btn').forEach(btn => {
         if (btn.innerText.includes('Download')) {
           btn.innerHTML = '🔒 Download';
         } else if (btn.innerText.includes('Preview')) {
           btn.innerHTML = '🔒 Preview';
         }
       });
     } else {
       document.querySelectorAll('.download-action-btn').forEach(btn => {
         if (btn.innerText.includes('Download')) {
           btn.innerHTML = '⬇️ Download';
         } else if (btn.innerText.includes('Preview')) {
           btn.innerHTML = '👁️ Preview';
         }
       });
     }
     */
   </script>

  <!-- ENQUIRY MODAL POPUP -->
  <div id="enquiryModal" class="fixed inset-0 z-50 hidden flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300">
    <div class="bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden transform scale-95 opacity-0 transition-all duration-300 flex flex-col max-h-[90vh]">
      <!-- Header -->
      <div class="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
        <div>
          <h3 class="text-xl font-bold text-slate-800">Request Project Information</h3>
          <p class="text-xs text-slate-500 mt-1">Fill in your details to receive complete project information.</p>
        </div>
        <button onclick="closeEnquiryModal()" class="text-slate-400 hover:text-slate-600 text-2xl font-bold transition-colors">&times;</button>
      </div>
      <!-- Body / Form -->
      <form id="enquiryForm" onsubmit="submitEnquiryForm(event)" class="p-6 overflow-y-auto space-y-4 no-scrollbar">
        <!-- Project Title (Readonly Auto Fill) -->
        <div>
          <label class="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Project Title</label>
          <input type="text" id="eqProjectTitle" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 font-semibold text-sm outline-none cursor-not-allowed" value="${title || project.title || 'MBA Project'}" readonly />
        </div>
        
        <!-- Full Name -->
        <div>
          <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Full Name <span class="text-red-500">*</span></label>
          <input type="text" id="eqName" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none" placeholder="Enter your full name" required />
        </div>

        <!-- Mobile Number & Email Address (Grid) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Mobile -->
          <div>
            <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Mobile Number <span class="text-red-500">*</span></label>
            <input type="number" id="eqMobile" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none" placeholder="Enter mobile number" required />
          </div>
          <!-- Email -->
          <div>
            <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email Address <span class="text-red-500">*</span></label>
            <input type="email" id="eqEmail" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none" placeholder="Enter email address" required />
          </div>
        </div>

        <!-- Message -->
        <div>
          <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Message</label>
          <textarea id="eqMessage" rows="3" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none resize-none">I am interested in this MBA project. Please share complete details and availability.</textarea>
        </div>

        <!-- Buttons -->
        <div class="flex flex-col sm:flex-row gap-3 pt-2">
          <button type="submit" class="flex-1 py-3 rounded-xl bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 text-white font-bold text-sm shadow-md hover:shadow-teal-100 transition-all transform active:scale-[0.98]">Submit Enquiry</button>
          <button type="button" onclick="closeEnquiryModal()" class="flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition-all">Cancel</button>
        </div>
      </form>
    </div>
  </div>

  <!-- TOAST NOTIFICATION -->
  <div id="enquiryToast" class="fixed bottom-5 left-1/2 transform -translate-x-1/2 z-50 hidden bg-slate-900 text-white px-6 py-4 rounded-xl shadow-2xl border border-slate-800 max-w-sm text-center transition-all duration-300">
    <div id="enquiryToastTitle" class="font-bold text-sm text-emerald-400 mb-1">Thank You!</div>
    <div id="enquiryToastMsg" class="text-xs text-slate-300">Your enquiry has been submitted successfully. Our team will contact you shortly.</div>
  </div>

  <script>
    // Enquiry modal functions
    window.openEnquiryModal = function() {
      try {
        window.parent.postMessage({ type: 'OPEN_ENQUIRY_MODAL' }, '*');
      } catch (msgErr) {}
    };

    window.closeEnquiryModal = function() {
      const modal = document.getElementById('enquiryModal');
      const modalBox = modal.querySelector('.transform');
      modalBox.classList.remove('scale-100', 'opacity-100');
      modalBox.classList.add('scale-95', 'opacity-0');
      modal.classList.remove('bg-slate-900/50');
      modal.classList.add('bg-slate-900/0');
      setTimeout(() => {
        modal.classList.add('hidden');
      }, 300);
    };

    window.submitEnquiryForm = async function(event) {
      event.preventDefault();
      
      const projectId = '${project.id || ''}';
      const projectTitle = document.getElementById('eqProjectTitle').value;
      const name = document.getElementById('eqName').value.trim();
      const mobile = document.getElementById('eqMobile').value.trim();
      const email = document.getElementById('eqEmail').value.trim();
      const college = "";
      const university = "";
      const specialization = "MBA - Project";
      const message = document.getElementById('eqMessage').value.trim();

      // Form validation
      if (!name || !mobile || !email) {
        alert("Please fill in all mandatory fields (Name, Mobile, and Email).");
        return;
      }
      
      if (mobile.length !== 10) {
        alert("Mobile number must be exactly 10 digits.");
        return;
      }

      if (!email.includes('@') || !email.includes('.')) {
        alert("Please enter a valid email address.");
        return;
      }

      try {
        const response = await fetch('${API_BASE_URL}/api/enquiries', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projectId: projectId,
            projectTitle: projectTitle,
            name: name,
            mobile: mobile,
            email: email,
            college: college,
            university: university,
            specialization: specialization,
            message: message
          })
        });

        const data = await response.json();
        
        if (response.ok && data.success) {
          // Success
          closeEnquiryModal();
          document.getElementById('enquiryForm').reset();
          
          // Show custom success toast
          const toast = document.getElementById('enquiryToast');
          toast.classList.remove('hidden');
          setTimeout(() => {
            toast.classList.add('hidden');
          }, 4000);
        } else {
          alert(data.detail || "Something went wrong while submitting the enquiry. Please try again.");
        }
      } catch (err) {
        console.error('Enquiry submission error:', err);
        alert("Failed to submit enquiry. Please check your network connection.");
      }
    };

    // Initialize Lucide Icons
    lucide.createIcons();
   </script>
  </body>
 </html>`;
};

export const GENERIC_PREVIEW_HTML_TEMPLATE = ({ title, specialization, project_type, original_price = '₹4999', discount_price = '₹2999', author = '', project = {} }) => {
  const techChips = project.tech_stack
    ? String(project.tech_stack).split(',').map(t => t.trim()).filter(Boolean)
    : [];

  const featuresList = project.features
    ? String(project.features).split('\n').map(f => f.trim()).filter(Boolean)
    : [];

  const badges = [
    project.category || specialization,
    project.domain || project_type
  ].filter(Boolean);

  const originalPriceVal = Number(project.price || 0);
  const discountedPriceVal = Number(project.discount_price || 0);
  const hasDiscount = discountedPriceVal > 0 && discountedPriceVal < originalPriceVal;

  let pricingCardHtml = '';
  if (hasDiscount) {
    const discountPercentage = Math.round(((originalPriceVal - discountedPriceVal) / originalPriceVal) * 100);
    pricingCardHtml = `
      <div class="w-full h-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-2 flex flex-col justify-center items-center text-center shadow-sm min-h-[56px] min-w-[160px]">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Price</span>
        <div class="flex items-baseline justify-center gap-2">
          <span class="text-xl font-extrabold text-emerald-600">₹${discountedPriceVal.toLocaleString()}</span>
          <span class="text-xs font-semibold text-slate-400 line-through">₹${originalPriceVal.toLocaleString()}</span>
        </div>
        <div class="mt-0.5">
          <span class="inline-block px-2 py-0.5 text-[9px] font-extrabold text-emerald-700 bg-emerald-50 rounded-full border border-emerald-100">${discountPercentage}% OFF</span>
        </div>
      </div>
    `;
  } else {
    const displayPrice = originalPriceVal > 0 ? `₹${originalPriceVal.toLocaleString()}` : 'Free';
    pricingCardHtml = `
      <div class="w-full h-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-2 flex flex-col justify-center items-center text-center shadow-sm min-h-[56px] min-w-[160px]">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Price</span>
        <span class="text-xl font-extrabold text-slate-800">${displayPrice}</span>
      </div>
    `;
  }

  const desc1 = project.description_1 || '';
  const desc2 = project.description_2 || '';
  const desc3 = project.description_3 || '';

  let finalDesc1 = desc1;
  let finalDesc2 = desc2;
  let finalDesc3 = desc3;

  if (!finalDesc1 && !finalDesc2 && !finalDesc3) {
    const fallbackText = project.description || project.about || project.abstract || 'No description available.';
    const paragraphs = fallbackText.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
    if (paragraphs.length >= 3) {
      finalDesc1 = paragraphs[0];
      finalDesc2 = paragraphs[1];
      finalDesc3 = paragraphs.slice(2).join('\n\n');
    } else if (paragraphs.length === 2) {
      finalDesc1 = paragraphs[0];
      finalDesc2 = paragraphs[1];
    } else {
      finalDesc1 = fallbackText;
    }
  }

  return `<!doctype html>
<html lang="en" class="h-full">
 <head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title || 'Project Details'}</title>
  <script src="https://cdn.tailwindcss.com/3.4.17"></script>
  <script src="https://cdn.jsdelivr.net/npm/lucide@0.263.0/dist/umd/lucide.min.js"></script>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&amp;display=swap" rel="stylesheet">
  <style>
    * { font-family: 'Inter', sans-serif; }
    .fade-in { animation: fadeIn 0.4s ease; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    body { box-sizing: border-box; }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
  </style>
 </head>
 <body class="h-full bg-white text-slate-900 overflow-auto">
  <div class="w-full min-h-full bg-slate-50 p-4 md:p-8 lg:p-12">
   <!-- TOP SECTION: Two aligned boxes -->
   <div class="max-w-7xl mx-auto mb-8">
    <div class="flex flex-col lg:flex-row gap-8 items-start">
     <!-- LEFT BOX: Image Gallery -->
     <div class="flex-1 lg:flex-[1.3] flex flex-col gap-4">
      <!-- Main Image -->
      <div class="w-full h-[240px] sm:h-[320px] md:h-[380px] lg:h-[420px] bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden relative group">
       <img id="mainImage" src="" alt="Project Preview" class="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" style="display:none;">
       <div id="mainPlaceholder" class="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-50">
        <div class="text-center">
         <i data-lucide="image" class="w-20 h-20 mx-auto text-slate-300 mb-3"></i>
         <p class="text-slate-500 text-sm font-medium">Project Preview</p>
        </div>
       </div>
      </div>
      <!-- Thumbnails -->
      <div class="grid grid-cols-6 gap-3" id="thumbnails"></div>
     </div>
     <!-- RIGHT BOX: Details -->
     <div class="flex-1 lg:flex-[0.9] lg:sticky lg:top-6">
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col gap-5">
       <div>
        <!-- Title -->
        <h1 id="titleEl" class="text-2xl md:text-3xl font-bold text-slate-900 mb-3 leading-tight">${title || project.title || 'Project'}</h1>
        <!-- Badges -->
        <div class="flex flex-wrap gap-1.5 mb-4">
         ${badges.map(b => '<span class="text-[11px] px-3 py-1.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200/80 font-medium">' + (project.category ? 'Software - ' : 'MBA - ') + b + '</span>').join('')}
        </div>
        <!-- Tech Stack -->
        <div class="mb-2 max-w-[440px]">
         <p class="text-[11px] text-slate-400 uppercase tracking-widest font-bold mb-1.5">TECH STACK</p>
         <div class="flex flex-wrap gap-1.5 py-0.5">
          ${techChips.map(tech => '<span class="chip text-[11px] px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-teal-500/20 font-medium transition-all duration-300 hover:border-teal-400 hover:bg-teal-50/50 hover:text-teal-700 whitespace-nowrap cursor-pointer">' + tech + '</span>').join('')}
         </div>
        </div>
       </div>
       <div>
        <div class="flex flex-col sm:flex-row items-stretch justify-center gap-4 w-full">
          <div class="flex-1">
            <button id="enquiryButton" onclick="openEnquiryModal()" class="w-full h-full py-4 rounded-xl bg-gradient-to-r from-teal-600 to-teal-500 text-white font-bold text-base transition-all duration-300 hover:shadow-md hover:from-teal-700 hover:to-teal-600 transform hover:-translate-y-0.5">Request Project</button>
          </div>
          <div class="flex-1">
            ${pricingCardHtml}
          </div>
        </div>
       </div>
      </div>
     </div>
    </div>
   </div>
   
   <!-- CATEGORY & DETAILS SINGLE CARD -->
   <div class="max-w-7xl mx-auto mb-12">
    <div class="bg-white rounded-[24px] border border-slate-200 shadow-sm p-6 md:p-8 hover:shadow-md transition-all duration-300 flex flex-col gap-8">
     
     <!-- 1. Category & Description -->
     <div>
      <div class="flex items-center gap-2 mb-4">
       <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">Category:</span>
       <span id="categoryEl" class="text-sm font-extrabold text-teal-600 bg-teal-50 border border-teal-100 rounded-full px-3 py-1">${project.category || 'Category'} ${project.domain ? '• ' + project.domain : ''}</span>
      </div>
      <h2 class="text-xl font-bold text-[#0f172a] mb-4 flex items-center gap-2">📄 Description</h2>
      <div class="relative border-l-2 border-slate-100 ml-3 pl-6 md:pl-8 space-y-6 py-1">
        <!-- Block 1 -->
        ${finalDesc1 ? `
        <div class="relative">
          <div class="absolute left-[-31px] md:left-[-37px] top-1.5 w-3.5 h-3.5 rounded-full bg-teal-500 border-4 border-white shadow-sm"></div>
          <h4 class="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Project Overview</h4>
          <p class="text-sm md:text-base text-slate-800 leading-relaxed font-normal">${finalDesc1}</p>
        </div>
        ` : ''}
        <!-- Block 2 -->
        ${finalDesc2 ? `
        <div class="relative">
          <div class="absolute left-[-31px] md:left-[-37px] top-1.5 w-3.5 h-3.5 rounded-full bg-teal-500 border-4 border-white shadow-sm"></div>
          <h4 class="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Implementation & Methodology</h4>
          <p class="text-sm md:text-base text-slate-800 leading-relaxed font-normal">${finalDesc2}</p>
        </div>
        ` : ''}
        <!-- Block 3 -->
        ${finalDesc3 ? `
        <div class="relative">
          <div class="absolute left-[-31px] md:left-[-37px] top-1.5 w-3.5 h-3.5 rounded-full bg-teal-500 border-4 border-white shadow-sm"></div>
          <h4 class="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Scope & Outcomes</h4>
          <p class="text-sm md:text-base text-slate-800 leading-relaxed font-normal">${finalDesc3}</p>
        </div>
        ` : ''}
      </div>
     </div>

     <!-- 2. Features -->
     ${featuresList.length > 0 ? `
     <hr class="border-slate-100" />
     <div>
      <h2 class="text-xl font-bold text-[#0f172a] mb-6 flex items-center gap-2">🌟 Key Features</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
       ${featuresList.map((feat, index) => {
        const colors = [
          { bg: 'bg-teal-50', text: 'text-teal-600', icon: 'sparkles' },
          { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'bar-chart-3' },
          { bg: 'bg-cyan-50', text: 'text-cyan-600', icon: 'activity' },
          { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'shield-check' },
          { bg: 'bg-indigo-50', text: 'text-indigo-600', icon: 'cloud' },
          { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'zap' }
        ];
        const color = colors[index % colors.length];

        let title = feat;
        let desc = '';
        if (feat.includes(':')) {
          const parts = feat.split(':');
          title = parts[0].trim();
          desc = parts.slice(1).join(':').trim();
        } else {
          const words = feat.split(' ');
          if (words.length > 3) {
            title = words.slice(0, 3).join(' ');
            desc = words.slice(3).join(' ');
          }
        }

        return '<div class="bg-white rounded-xl border border-slate-200 shadow-sm p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">' +
          '<div class="w-12 h-12 rounded-lg ' + color.bg + ' flex items-center justify-center mb-4">' +
          '<i data-lucide="' + color.icon + '" class="w-6 h-6 ' + color.text + '"></i>' +
          '</div>' +
          '<h3 class="font-bold text-slate-900 mb-2">' + title + '</h3>' +
          (desc ? '<p class="text-sm text-slate-600">' + desc + '</p>' : '') +
          '</div>';
       }).join('')}
      </div>
     </div>
     ` : ''}

     <!-- 3. Tech Stack -->
     ${techChips.length > 0 ? `
     <hr class="border-slate-100" />
     <div>
      <h3 class="text-xl font-bold text-[#0f172a] mb-6 flex items-center gap-3"><i data-lucide="cpu" class="w-6 h-6 text-teal-600"></i> Technologies Used</h3>
      <div class="flex flex-wrap gap-2.5">
       ${techChips.map(tech => '<span class="chip text-sm px-4 py-2 rounded-full bg-slate-100 text-slate-700 border border-teal-200 font-medium hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700 transition-all duration-300 cursor-pointer">' + tech + '</span>').join('')}
      </div>
     </div>
     ` : ''}

     <!-- 4. About Project -->
     ${project.about ? `
     <hr class="border-slate-100" />
     <div>
      <h3 class="text-xl font-bold text-[#0f172a] mb-6">About This Project</h3>
      <div class="text-slate-700 leading-relaxed whitespace-pre-line">${project.about}</div>
     </div>
     ` : ''}

     <!-- 5. Setup & Installation -->
     <hr class="border-slate-100" />
     <div>
      <h3 class="text-xl font-bold text-[#0f172a] mb-6 flex items-center gap-2">🛠️ Setup & Installation</h3>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
       
       <div class="space-y-6">
        <!-- Video Row Cards -->
        <div class="installation-video-wrap flex flex-col gap-4">
         ${project.demo_video_url ? `
         <button class="video-row-card w-full text-left p-4 rounded-xl border border-slate-200 hover:border-teal-400 hover:bg-teal-50/20 transition-all flex items-center gap-4" onclick="playVideo('${project.demo_video_url}', 'Demo Video')">
          <div class="vrc-play-box w-10 h-10 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-bold">▶</div>
          <div class="vrc-info"><span class="vrc-title font-semibold text-slate-800">Demo Video</span></div>
         </button>
         ` : ''}

         ${project.installation_video_url ? `
         <button class="video-row-card locked w-full text-left p-4 rounded-xl border border-slate-200 hover:border-teal-400 hover:bg-teal-50/20 transition-all flex items-center gap-4" onclick="playVideo(null, 'Installation Guide', 'Setup Instructions', true)">
          <div class="vrc-play-box w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold">🔒</div>
          <div class="vrc-info"><span class="vrc-title font-semibold text-slate-800">Installation Guide Video</span></div>
         </button>
         ` : ''}
        </div>

        <!-- Step-by-Step Instructions -->
        <div class="installation-steps bg-slate-50 p-6 rounded-xl border border-slate-100">
         <h4 class="font-bold text-slate-800 mb-3">Setup Instructions</h4>
         <div class="setup-section font-medium text-slate-700 whitespace-pre-line text-sm">
          ${project.setup_info ? project.setup_info : 'No installation steps provided. Contact the author for details.'}
         </div>
        </div>
       </div>

       <!-- Downloads Card -->
       <div class="bg-slate-50 p-6 rounded-xl border border-slate-100 flex flex-col gap-4">
        <h4 class="font-bold text-slate-800 mb-1">Downloads</h4>
        <p class="text-xs text-slate-500 mb-2">Access source files, reports, and documentation packages.</p>
        <div class="flex flex-col gap-3">
         ${project.installation_file ? `<button class="download-btn w-full py-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-all" onclick="downloadProjectFile('installation_file')">Download Installation Package</button>` : ''}
         ${project.zip_file ? `<button class="download-btn w-full py-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-all" onclick="downloadProjectFile('zip')">Download Source ZIP</button>` : ''}
         ${project.report_file ? `<button class="download-btn w-full py-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-all" onclick="downloadProjectFile('report')">Download Report</button>` : ''}
         ${project.doc_file ? `<button class="download-btn w-full py-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-all" onclick="downloadProjectFile('doc')">Download Docs</button>` : ''}
         ${!project.installation_file && !project.zip_file && !project.report_file && !project.doc_file ? '<p class="text-sm text-slate-500 text-center py-4">No download files attached.</p>' : ''}
        </div>
       </div>

      </div>
     </div>

    </div>
   </div>

  <!-- Video Modal Markup -->
  <div id="videoModal" class="fixed inset-0 bg-slate-900/90 z-50 hidden flex items-center justify-center p-4">
    <div class="bg-white rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl">
      <div class="p-6 border-b border-slate-100 flex items-center justify-between">
        <h3 id="videoModalTitle" class="text-xl font-bold text-slate-900">Project Video</h3>
        <button onclick="closeVideoModal()" class="text-slate-400 hover:text-slate-600 text-2xl font-bold">✕</button>
      </div>
      <div class="aspect-video bg-black">
        <video id="modalVideoPlayer" controls class="w-full h-full"></video>
      </div>
    </div>
  </div>

  <!-- ENQUIRY MODAL POPUP -->
  <div id="enquiryModal" class="fixed inset-0 z-50 hidden flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300">
    <div class="bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden transform scale-95 opacity-0 transition-all duration-300 flex flex-col max-h-[90vh]">
      <!-- Header -->
      <div class="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
        <div>
          <h3 class="text-xl font-bold text-slate-800">Request Project Information</h3>
          <p class="text-xs text-slate-500 mt-1">Fill in your details to receive complete project information.</p>
        </div>
        <button onclick="closeEnquiryModal()" class="text-slate-400 hover:text-slate-600 text-2xl font-bold transition-colors">&times;</button>
      </div>
      <!-- Body / Form -->
      <form id="enquiryForm" onsubmit="submitEnquiryForm(event)" class="p-6 overflow-y-auto space-y-4 no-scrollbar">
        <!-- Project Title (Readonly Auto Fill) -->
        <div>
          <label class="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Project Title</label>
          <input type="text" id="eqProjectTitle" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 font-semibold text-sm outline-none cursor-not-allowed" value="${title || project.title || 'Project'}" readonly />
        </div>
        
        <!-- Full Name -->
        <div>
          <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Full Name <span class="text-red-500">*</span></label>
          <input type="text" id="eqName" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none" placeholder="Enter your full name" required />
        </div>

        <!-- Mobile Number & Email Address (Grid) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Mobile -->
          <div>
            <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Mobile Number <span class="text-red-500">*</span></label>
            <input type="number" id="eqMobile" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none" placeholder="Enter mobile number" required />
          </div>
          <!-- Email -->
          <div>
            <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email Address <span class="text-red-500">*</span></label>
            <input type="email" id="eqEmail" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none" placeholder="Enter email address" required />
          </div>
        </div>

        <!-- Message -->
        <div>
          <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Message</label>
          <textarea id="eqMessage" rows="3" class="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-800 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all outline-none resize-none">I am interested in this project. Please share complete details and availability.</textarea>
        </div>

        <!-- Buttons -->
        <div class="flex flex-col sm:flex-row gap-3 pt-2">
          <button type="submit" class="flex-1 py-3 rounded-xl bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 text-white font-bold text-sm shadow-md hover:shadow-teal-100 transform active:scale-[0.98]">Submit Enquiry</button>
          <button type="button" onclick="closeEnquiryModal()" class="flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition-all">Cancel</button>
        </div>
      </form>
    </div>
  </div>

  <!-- TOAST NOTIFICATION -->
  <div id="enquiryToast" class="fixed bottom-5 left-1/2 transform -translate-x-1/2 z-50 hidden bg-slate-900 text-white px-6 py-4 rounded-xl shadow-2xl border border-slate-800 max-w-sm text-center transition-all duration-300">
    <div id="enquiryToastTitle" class="font-bold text-sm text-emerald-400 mb-1">Thank You!</div>
    <div id="enquiryToastMsg" class="text-xs text-slate-300">Your enquiry has been submitted successfully. Our team will contact you shortly.</div>
  </div>

  <script>
    // Populate images and thumbnails
    const images = ${JSON.stringify(project.images || [])};
    const mainImage = document.getElementById('mainImage');
    const mainPlaceholder = document.getElementById('mainPlaceholder');
    const thumbnailsDiv = document.getElementById('thumbnails');

    if (images && images.length > 0) {
      mainImage.src = images[0];
      mainImage.style.display = 'block';
      mainPlaceholder.style.display = 'none';

      thumbnailsDiv.innerHTML = '';
      images.forEach((imgUrl, index) => {
        const btn = document.createElement('button');
        btn.className = 'thumb-item group aspect-video rounded-xl overflow-hidden flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md hover:scale-105 border relative ' + 
          (index === 0 
            ? 'thumb-active border-teal-500 ring-4 ring-teal-500/20 brightness-105 opacity-100' 
            : 'border-slate-200/80 opacity-80');
        
        btn.dataset.index = index;
        btn.innerHTML = '<img src="' + imgUrl + '" class="w-full h-full object-cover transition-all duration-500" />' +
          '<div class="absolute inset-0 bg-slate-50/20 backdrop-blur-[1.5px] transition-all duration-300 group-hover:backdrop-blur-none group-hover:bg-transparent ' + (index === 0 ? 'hidden' : '') + '"></div>';
        
        btn.addEventListener('click', () => {
          document.querySelectorAll('.thumb-item').forEach((t, i) => { 
              t.classList.remove('thumb-active', 'border-teal-500', 'ring-4', 'ring-teal-500/20', 'brightness-105', 'opacity-100');
              t.classList.add('border-slate-200/80', 'opacity-80');
              const overlay = t.querySelector('.absolute');
              if (overlay) overlay.classList.remove('hidden');
          });
          btn.classList.add('thumb-active', 'border-teal-500', 'ring-4', 'ring-teal-500/20', 'brightness-105', 'opacity-100');
          btn.classList.remove('border-slate-200/80', 'opacity-80');
          const overlay = btn.querySelector('.absolute');
          if (overlay) overlay.classList.add('hidden');
          
          mainImage.src = imgUrl;
        });
        thumbnailsDiv.appendChild(btn);
      });
    } else {
      mainImage.style.display = 'none';
      mainPlaceholder.style.display = 'flex';
      thumbnailsDiv.style.display = 'none';
    }

    // Purchase check
    const projectId = '${project.id || ''}';
    const price = Number('${project.price || 0}');
    const isFree = !price || price === 0;
    const orderId = localStorage.getItem('purchase_order_' + projectId);
    const token = orderId ? localStorage.getItem('purchase_token_' + orderId) : null;
    const isPurchased = isFree || (orderId && token);

    const buyBtn = document.getElementById('buyButton');
    if (buyBtn) {
      if (isPurchased) {
        buyBtn.innerHTML = '🔓 Project Unlocked';
        buyBtn.disabled = true;
        buyBtn.className = 'w-full py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-bold text-lg cursor-not-allowed opacity-80';
      } else {
        buyBtn.addEventListener('click', function() {
          window.parent.postMessage({ type: 'BUY_NOW' }, '*');
        });
      }
    }

    // Update download buttons
    if (!isPurchased) {
      document.querySelectorAll('.download-btn').forEach(btn => {
        btn.innerHTML = '🔒 Locked';
      });
    }

    // Video playing functionality
    window.playVideo = function(videoUrl, title, isSecure = false) {
      if (isSecure) {
        const orderId = localStorage.getItem('purchase_order_' + projectId);
        const token = orderId ? localStorage.getItem('purchase_token_' + orderId) : null;
        if (!isFree && (!orderId || !token)) {
          alert('Please purchase the project to view the installation guide.');
          window.parent.postMessage({ type: 'BUY_NOW' }, '*');
          return;
        }
        if (orderId && token) {
          const filename = videoUrl.split('/').pop();
          videoUrl = '${API_BASE_URL}/api/payments/secure-download/files/' + orderId + '/' + filename + '?token=' + token;
        }
      }
      
      const modal = document.getElementById('videoModal');
      const titleEl = document.getElementById('videoModalTitle');
      const player = document.getElementById('modalVideoPlayer');
      
      titleEl.textContent = title;
      player.src = videoUrl;
      modal.classList.remove('hidden');
      player.play().catch(e => console.log('Auto-play blocked or failed', e));
    };

    window.closeVideoModal = function() {
      const modal = document.getElementById('videoModal');
      const player = document.getElementById('modalVideoPlayer');
      modal.classList.add('hidden');
      player.pause();
      player.src = '';
    };

    // Download functionality
    window.downloadProjectFile = function(fileType) {
      const orderId = localStorage.getItem('purchase_order_' + projectId);
      const token = orderId ? localStorage.getItem('purchase_token_' + orderId) : null;

      if (!isFree && (!orderId || !token)) {
        window.parent.postMessage({ type: 'BUY_NOW' }, '*');
        return;
      }

      const backendBase = '${API_BASE_URL}';
      let filename = null;
      if (fileType === 'zip') {
        const zipFile = '${project.zip_file || ''}';
        filename = zipFile ? zipFile.split('/').pop() : null;
      } else if (fileType === 'report') {
        const reportFile = '${project.report_file || project.pdf_file || ''}';
        filename = reportFile ? reportFile.split('/').pop() : null;
      }

      if (filename && orderId && token) {
        const url = backendBase + '/api/payments/secure-download/files/' + orderId + '/' + filename + '?token=' + token;
        window.open(url, '_blank');
      } else {
        let directUrl = '';
        if (fileType === 'zip') directUrl = '${project.zip_file || ''}';
        if (fileType === 'report') directUrl = '${project.report_file || project.pdf_file || ''}';
        
        if (directUrl) {
          window.open(directUrl, '_blank');
        } else {
          alert('File not available for download.');
        }
      }
    };

    window.openEnquiryModal = function() {
      try {
        window.parent.postMessage({ type: 'OPEN_ENQUIRY_MODAL' }, '*');
      } catch (msgErr) {}
    };

    window.closeEnquiryModal = function() {
      const modal = document.getElementById('enquiryModal');
      const modalBox = modal.querySelector('.transform');
      modalBox.classList.remove('scale-100', 'opacity-100');
      modalBox.classList.add('scale-95', 'opacity-0');
      modal.classList.remove('bg-slate-900/50');
      modal.classList.add('bg-slate-900/0');
      setTimeout(() => {
        modal.classList.add('hidden');
      }, 300);
    };

    window.submitEnquiryForm = async function(event) {
      event.preventDefault();
      
      const projectId = '${project.id || ''}';
      const projectTitle = document.getElementById('eqProjectTitle').value;
      const name = document.getElementById('eqName').value.trim();
      const mobile = document.getElementById('eqMobile').value.trim();
      const email = document.getElementById('eqEmail').value.trim();
      const college = "";
      const university = "";
      const specialization = "Software - Project";
      const message = document.getElementById('eqMessage').value.trim();

      // Form validation
      if (!name || !mobile || !email) {
        alert("Please fill in all mandatory fields (Name, Mobile, and Email).");
        return;
      }
      
      if (mobile.length !== 10) {
        alert("Mobile number must be exactly 10 digits.");
        return;
      }

      if (!email.includes('@') || !email.includes('.')) {
        alert("Please enter a valid email address.");
        return;
      }

      try {
        const response = await fetch('${API_BASE_URL}/api/enquiries', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projectId: projectId,
            projectTitle: projectTitle,
            name: name,
            mobile: mobile,
            email: email,
            college: college,
            university: university,
            specialization: specialization,
            message: message
          })
        });

        const data = await response.json();
        
        if (response.ok && data.success) {
          // Success
          closeEnquiryModal();
          document.getElementById('enquiryForm').reset();
          
          // Show custom success toast
          const toast = document.getElementById('enquiryToast');
          toast.classList.remove('hidden');
          setTimeout(() => {
            toast.classList.add('hidden');
          }, 4000);
        } else {
          alert(data.detail || "Something went wrong while submitting the enquiry. Please try again.");
        }
      } catch (err) {
        console.error('Enquiry submission error:', err);
        alert("Failed to submit enquiry. Please check your network connection.");
      }
    };

    // Initialize Lucide Icons
    lucide.createIcons();
  </script>
 </body>
</html>`;
};

export function openPremiumView(project = {}) {
  try {
    const source = (project.__tablename__ === 'mba_projects' || project.pricing_type || project.specialization)
      ? 'mba'
      : 'project';

    if (!project?.id) {
      alert('Unable to open project preview: missing project id.');
      return;
    }

    const previewUrl = `/premium-preview/${project.id}?source=${source}`;
    // Change window.open to setting window.location.href to open in the same tab
    window.location.href = previewUrl;
  } catch (err) {
    console.error('Failed to open premium view', err);
    alert('Unable to open detailed view. See console for details.');
  }
}

/* ─── Video Fullscreen Modal ─────────────────────────────── */
const VideoModal = ({ src, title, subtitle, onClose }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.play().catch(() => { });
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="video-modal-overlay" onClick={onClose}>
      <div className="video-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="video-modal-header">
          <div className="video-modal-title-group">
            <div className="video-modal-play-dot">▶</div>
            <div>
              <h3 className="video-modal-title">{title}</h3>
              <p className="video-modal-subtitle">{subtitle}</p>
            </div>
          </div>
          <button className="video-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="video-modal-player-wrap">
          <video ref={videoRef} src={src} controls autoPlay className="video-modal-player" />
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────── */
const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [activeTab, setActiveTab] = useState("description");
  const [videoModal, setVideoModal] = useState(null);
  const [isPurchased, setIsPurchased] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isMba, setIsMba] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let data = null;
        const sourceParam = typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('source')
          : null;
        const isMbaRoute = typeof window !== 'undefined'
          && (window.location.pathname.startsWith('/mba-projects') || sourceParam === 'mba');

        if (isMbaRoute) {
          // MBA project: try mbaAPI then fallback to public fetch
          try {
            const resp = await mbaAPI.getProjectById(id);
            data = resp.data;
          } catch (axiosErr) {
            console.warn('mbaAPI.getProjectById failed, falling back to fetch', axiosErr);
            const projRes = await fetch(`${API_BASE_URL}/api/mba-projects/${id}`, { credentials: 'omit' });
            if (!projRes.ok) {
              if (projRes.status === 401) {
                console.warn('ProjectDetail_Premium.jsx: fetch MBA project returned 401');
                alert('You are not authorized to view this project. Please login.');
                return;
              }
              let bodyText = '';
              try { bodyText = await projRes.text(); } catch (readErr) { bodyText = String(readErr); }
              console.error('ProjectDetail_Premium.jsx: fetch MBA project returned non-OK status', projRes.status, bodyText);
              alert('MBA Project not found or server error. Check console for details.');
              return;
            }
            data = await projRes.json();
          }
        } else {
          // regular project flow (public-first)
          try {
            const publicResp = await fetch(`${API_BASE_URL}/api/projects/public`, { credentials: 'omit' });
            if (publicResp.ok) {
              const publicJson = await publicResp.json();
              const publicProject = Array.isArray(publicJson?.data)
                ? publicJson.data.find((item) => String(item.id) === String(id))
                : null;
              if (publicProject) data = publicProject;
            }
          } catch (publicErr) {
            console.warn('Public project list fetch failed; trying direct endpoint', publicErr);
          }

          if (!data) {
            try {
              const resp = await projectsAPI.getById(id);
              data = resp.data;
            } catch (axiosErr) {
              console.warn('projectsAPI.getById failed, falling back to fetch', axiosErr);
              const projRes = await fetch(`${API_BASE_URL}/api/projects/${id}`, { credentials: 'omit' });
              if (!projRes.ok) {
                if (projRes.status === 401) {
                  console.warn('ProjectDetail_Premium.jsx: fetch project returned 401');
                  alert('You are not authorized to view this project. Please login.');
                  return;
                }
                // Log response body for easier debugging
                let bodyText = '';
                try { bodyText = await projRes.text(); } catch (readErr) { bodyText = String(readErr); }
                console.error('ProjectDetail_Premium.jsx: fetch project returned non-OK status', projRes.status, bodyText);
                alert('Project not found or server error. Check console for details.');
                return;
              }
              data = await projRes.json();
            }
          }
        }

        let parsedImages = [];
        try {
          if (data?.images) {
            parsedImages = typeof data.images === 'string' ? JSON.parse(data.images) : data.images;
          }
        } catch (e) {
          console.error("Error parsing images", e);
        }

        const updatedProject = { ...data, images: parsedImages };
        setProject(updatedProject);
        // mark MBA pages when the route or returned record indicates MBA
        setIsMba(
          (typeof window !== 'undefined' && (
            window.location.pathname.startsWith('/mba-projects')
            || new URLSearchParams(window.location.search).get('source') === 'mba'
          ))
          || Boolean(updatedProject.__tablename__ === 'mba_projects' || updatedProject.pricing_type || updatedProject.specialization)
        );
        if (parsedImages.length) setActiveImage(getImageUrl(parsedImages[0]));

        setIsPurchased(false);
      } catch (err) {
        // More detailed logging to help diagnose network/JSON/axios errors
        try {
          if (err?.response) {
            console.error('ProjectDetail_Premium.jsx: axios error', err.response.status, err.response.data);
          } else {
            console.error("ProjectDetail_Premium.jsx: Failed to fetch project data:", err);
          }
        } catch (logErr) {
          console.error('ProjectDetail_Premium.jsx: error while logging original error', logErr);
          console.error(err);
        }
        alert('Failed to fetch project data. Check console for details.');
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [id]);

  const handleDownload = (fileType) => {
    alert('Please submit a request to access project files.');
  };

  const handleViewMore = () => {
    const source = (project.__tablename__ === 'mba_projects' || project.pricing_type || project.specialization)
      ? 'mba'
      : 'project';
    navigate(`/premium-preview/${project.id}?source=${source}`);
  };

  const openVideo = useCallback((src, title, subtitle, isSecure = false) => {
    if (isSecure && !isPurchased) {
      alert('Please purchase the project to watch the installation guide.');
      return;
    }
    setVideoModal({ src, title, subtitle });
  }, [isPurchased]);

  const closeVideo = useCallback(() => setVideoModal(null), []);

  if (!project) {
    return (
      <div className="project-detail-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading project details…</p>
        </div>
      </div>
    );
  }

  const techStack = project.tech_stack ? project.tech_stack.split(",").map(t => t.trim()) : [];
  const features = project.features ? project.features.split("\n").filter(f => f.trim()) : [];

  const tabItems = [
    { id: 'description', label: 'Overview' },
    { id: 'features', label: 'Features' },
    { id: 'tech', label: 'Tech Stack' },
    { id: 'modules', label: 'Modules' },
    { id: 'installation', label: 'Installation' },
  ];

  return (
    <div className="project-detail-page">
      {videoModal && (
        <VideoModal src={videoModal.src} title={videoModal.title} subtitle={videoModal.subtitle} onClose={closeVideo} />
      )}

      <section className="hero-section">
        <div className="hero-container">
          {isMba ? (
            <div className="mba-hero-simple">
              <div className="mba-info">
                <h1 className="project-title">{project.title}</h1>
                <div className="mba-meta">
                  <div style={{ fontWeight: 700 }}>{`Type: ${project.type || '-'}${project.specialization ? ` • ${project.specialization}` : ''}`}</div>
                  {(project.author || project.owner || project.contact_name) && (
                    <div className="mba-author" style={{ marginTop: 6, color: '#64748B' }}>{project.author || project.owner || project.contact_name}</div>
                  )}
                  <div style={{ marginTop: 6 }}><strong>Price:</strong> {project.price ? `₹${project.price?.toLocaleString()}` : (project.price_formatted || 'Contact')}</div>
                </div>
                <div className="mba-abstract" dangerouslySetInnerHTML={{ __html: project.abstract || project.description || 'No description available.' }} />
                <div className="mba-files">
                  {project.report_file && <a className="file-pill" href={getImageUrl(project.report_file)} target="_blank" rel="noreferrer">PDF</a>}
                  {project.zip_file && <a className="file-pill" href={getImageUrl(project.zip_file)} target="_blank" rel="noreferrer">DOC</a>}
                </div>
                <div className="hero-buttons">
                  {isPurchased ? (
                    <button className="btn btn-outline btn-unlocked" disabled>🔓 Project Unlocked</button>
                  ) : (
                    <button className="btn btn-primary btn-buy" onClick={() => navigate(`/request?projectId=${id}`)} disabled={isPaymentLoading}>Request Access</button>
                  )}
                  <button className="btn btn-secondary btn-view" onClick={handleViewMore} style={{ marginLeft: 12 }}>🔎 View More</button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="hero-gallery">
                <div className="main-image-wrapper">
                  {activeImage && <div className="main-image-bg" style={{ backgroundImage: `url(${activeImage})` }} />}
                  {activeImage ? <img src={activeImage} alt={project.title} className="main-image" /> : <div className="no-preview">No Preview Available</div>}
                  <div className="image-badge">Preview</div>
                </div>

                {project.images && project.images.length > 1 && (
                  <div className="thumbnails">
                    {project.images.map((img, i) => (
                      <div
                        key={i}
                        className={`thumbnail ${activeImage === getImageUrl(img) ? 'active' : ''}`}
                        onClick={() => setActiveImage(getImageUrl(img))}
                      >
                        <img src={getImageUrl(img)} alt={`Thumb ${i + 1}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="hero-info">
                <h1 className="project-title">{project.title}</h1>
                <div className="price-section">
                  <span className="price-label">Price</span>
                  <h2 className="price">₹{project.price?.toLocaleString()}</h2>
                  {isPurchased && <span className="purchased-badge">✓ Purchased</span>}
                </div>
                {/* Tech stack removed per request */}
                <div className="hero-buttons">
                  {isPurchased ? (
                    <button className="btn btn-outline btn-unlocked" disabled>🔓 Project Unlocked</button>
                  ) : (
                    <button className="btn btn-primary btn-buy" onClick={() => navigate(`/request?projectId=${id}`)} disabled={isPaymentLoading}>Request Access</button>
                  )}
                  <button className="btn btn-secondary btn-view" onClick={handleViewMore} style={{ marginLeft: 12 }}>🔎 View More</button>
                </div>
                {/* WhatsApp contact removed per request */}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="project-details-single-card-section" style={{ maxWidth: '1280px', margin: '0 auto 32px', padding: '0 16px' }}>
        <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid #e2e8f0', padding: '32px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)', display: 'flex', flexDirection: 'column', gap: '32px' }}>
          
          {/* 1. Category & Description */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category:</span>
              <span style={{ fontSize: '14px', fontWeight: 800, color: '#0d9488', background: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: '999px', padding: '4px 12px' }}>
                {project.category} {project.domain ? `• ${project.domain}` : ''}
              </span>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>📄 Description</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', color: '#334155', lineHeight: '1.7' }}>
              {[1, 2, 3, 4, 5, 6].map((n) => (project[`description_${n}`] ? <p key={n}>{project[`description_${n}`]}</p> : null))}
            </div>
          </div>

          {/* 2. Features */}
          {features.length > 0 && (
            <>
              <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: 0 }} />
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '24px' }}>🌟 Key Features</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                  {features.map((feat, index) => {
                    let title = feat;
                    let desc = '';
                    if (feat.includes(':')) {
                      const parts = feat.split(':');
                      title = parts[0].trim();
                      desc = parts.slice(1).join(':').trim();
                    } else {
                      const words = feat.split(' ');
                      if (words.length > 3) {
                        title = words.slice(0, 3).join(' ');
                        desc = words.slice(3).join(' ');
                      }
                    }
                    return (
                      <div key={index} style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
                        <h3 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>{title}</h3>
                        {desc && <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: 0 }}>{desc}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* 3. Technologies */}
          {techStack.length > 0 && (
            <>
              <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: 0 }} />
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>💻 Technologies Used</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {techStack.map((tech, i) => (
                    <span key={i} style={{ fontSize: '14px', padding: '8px 16px', borderRadius: '999px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', fontWeight: 500 }}>
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* 4. About Project */}
          {project.about && (
            <>
              <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: 0 }} />
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>About This Project</h3>
                <div style={{ color: '#334155', lineHeight: '1.7', whiteSpace: 'pre-line' }}>{project.about}</div>
              </div>
            </>
          )}

          {/* 5. Setup & Installation */}
          <>
            <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: 0 }} />
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '24px' }}>🛠️ Setup & Installation</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  
                  {/* Videos */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {project.demo_video_url && (
                      <button 
                        onClick={() => openVideo(getImageUrl(project.demo_video_url), 'Demo Video', 'See the project in action')}
                        style={{ padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer', textAlign: 'left', width: '100%', fontWeight: 600 }}
                      >
                        <span style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#ccfbf1', color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>▶</span>
                        <span>Demo Video</span>
                      </button>
                    )}

                    {project.installation_video_url && (
                      <button 
                        onClick={() => openVideo(null, 'Installation Guide', 'Setup Instructions', true)}
                        style={{ padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer', textAlign: 'left', width: '100%', fontWeight: 600 }}
                      >
                        <span style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#f1f5f9', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>🔒</span>
                        <span>Installation Guide Video</span>
                      </button>
                    )}
                  </div>

                  {/* Instructions */}
                  <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                    <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '12px', margin: 0 }}>Setup Instructions</h4>
                    <pre style={{ margin: '12px 0 0 0', fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: '14px', color: '#334155' }}>
                      {project.setup_info || 'No installation steps provided. Contact the author for details.'}
                    </pre>
                  </div>
                </div>

                {/* Downloads */}
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '4px', margin: 0 }}>Downloads</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Access source files, reports, and documentation packages.</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                    {project.installation_file && <button onClick={() => handleDownload('installation_file')} style={{ padding: '12px', borderRadius: '8px', background: '#0f766e', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Download Installation Package</button>}
                    {project.zip_file && <button onClick={() => handleDownload('zip')} style={{ padding: '12px', borderRadius: '8px', background: '#0f766e', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Download Source ZIP</button>}
                    {project.report_file && <button onClick={() => handleDownload('report')} style={{ padding: '12px', borderRadius: '8px', background: '#0f766e', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Download Report</button>}
                    {project.doc_file && <button onClick={() => handleDownload('doc')} style={{ padding: '12px', borderRadius: '8px', background: '#0f766e', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Download Docs</button>}
                    {!project.installation_file && !project.zip_file && !project.report_file && !project.doc_file && <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', margin: '16px 0' }}>No download files attached.</p>}
                  </div>
                </div>
              </div>
            </div>
          </>

        </div>
      </section>

      <a
        href={project?.whatsapp_number ? `https://wa.me/${String(project.whatsapp_number).replace(/\D/g, '')}` : 'https://wa.me/910000000000'}
        target="_blank"
        rel="noreferrer"
        aria-label="WhatsApp"
        style={{
          position: 'fixed',
          right: '18px',
          bottom: '18px',
          zIndex: 9999,
          width: '58px',
          height: '58px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
          boxShadow: '0 14px 30px rgba(34, 197, 94, 0.25)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          textDecoration: 'none',
        }}
      >
        💬
      </a>
    </div>
  );
};

export default ProjectDetail;
